import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import api from "../../utills/api";

const PLATFORM_OPTIONS = [
  "Meta",
  "Google",
  "YouTube",
  "WhatsApp",
  "Website",
  "Other",
];

const OBJECTIVE_OPTIONS = [
  "Lead Generation",
  "Admissions",
  "Brand Awareness",
  "Website Traffic",
  "Other",
];

const PUBLISH_STATUS_OPTIONS = [
  "Not Started",
  "Pending",
  "Partial",
  "Published",
  "Failed",
];

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

const getPublishBadge = (status) => {
  switch (status) {
    case "Published":
      return <Badge color="success">Published</Badge>;

    case "Pending":
      return <Badge color="warning">Pending</Badge>;

    case "Partial":
      return <Badge color="info">Partial</Badge>;

    case "Failed":
      return <Badge color="danger">Failed</Badge>;

    default:
      return <Badge color="secondary">Not Started</Badge>;
  }
};

const getActiveBadge = (isActive) =>
  isActive ? (
    <Badge color="success">Active</Badge>
  ) : (
    <Badge color="secondary">Inactive</Badge>
  );

const getPublishingLabel = (platform) => {
  switch (platform) {
    case "Google":
      return "Google Publishing";
    case "Meta":
      return "Meta Publishing";
    case "YouTube":
      return "YouTube";
    default:
      return "Publishing";
  }
};

const normalizeCampaign = (campaign) => ({
  ...campaign,
  id: campaign?._id || campaign?.id,
});

const Campaigns = () => {
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [search, setSearch] = useState("");
  const [platform, setPlatform] = useState("");
  const [objective, setObjective] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [publishStatus, setPublishStatus] = useState("");

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });

  const [selectedCampaigns, setSelectedCampaigns] = useState([]);

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = {
        page,
        limit: 20,
      };

      if (search.trim()) params.search = search.trim();
      if (platform) params.platform = platform;
      if (objective) params.objective = objective;
      if (activeFilter !== "") params.isActive = activeFilter;
      if (publishStatus) params.metaPublishStatus = publishStatus;

      const response = await api.get("/campaigns", { params });

      if (!response?.data?.success) {
        throw new Error(
          response?.data?.message || "Unable to fetch campaigns."
        );
      }

      const result = response?.data?.data || {};
      const campaignList =
        result?.campaigns ||
        result?.data ||
        [];

      const normalized = Array.isArray(campaignList)
        ? campaignList.map(normalizeCampaign)
        : [];

      setCampaigns(normalized);

      setSelectedCampaigns((previous) =>
        previous.filter((id) =>
          normalized.some((campaign) => campaign.id === id)
        )
      );

      setPagination(
        result?.pagination || {
          page,
          limit: 20,
          total: normalized.length,
          pages: 1,
        }
      );
    } catch (err) {
      console.error("Failed to fetch campaigns:", err);

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to fetch campaigns."
      );
    } finally {
      setLoading(false);
    }
  }, [
    page,
    search,
    platform,
    objective,
    activeFilter,
    publishStatus,
  ]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const stats = useMemo(() => {
    return {
      total: campaigns.length,
      active: campaigns.filter((campaign) => campaign.isActive).length,
      leads: campaigns.reduce(
        (sum, campaign) => sum + Number(campaign.leadCount || 0),
        0
      ),
      applications: campaigns.reduce(
        (sum, campaign) =>
          sum + Number(campaign.applicationCount || 0),
        0
      ),
      admissions: campaigns.reduce(
        (sum, campaign) =>
          sum + Number(campaign.admissionCount || 0),
        0
      ),
      spend: campaigns.reduce(
        (sum, campaign) => sum + Number(campaign.spend || 0),
        0
      ),
    };
  }, [campaigns]);

  const performCampaignAction = async (campaign, action) => {
    if (!campaign?.id) return;

    try {
      setActionLoading(`${action}-${campaign.id}`);
      setError("");
      setSuccess("");

      let response;

      if (action === "activate") {
        response = await api.put(
          `/campaigns/${campaign.id}/activate`
        );
      }

      if (action === "deactivate") {
        response = await api.put(
          `/campaigns/${campaign.id}/deactivate`
        );
      }

      if (action === "delete") {
        const confirmed = window.confirm(
          `Delete campaign "${campaign.name}"?`
        );

        if (!confirmed) return;

        response = await api.delete(
          `/campaigns/${campaign.id}`
        );
      }

      if (!response?.data?.success) {
        throw new Error(
          response?.data?.message ||
            "Campaign action failed."
        );
      }

      setSuccess(
        action === "activate"
          ? "Campaign activated successfully."
          : action === "deactivate"
          ? "Campaign deactivated successfully."
          : "Campaign deleted successfully."
      );

      await fetchCampaigns();
    } catch (err) {
      console.error("Campaign action failed:", err);

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Campaign action failed."
      );
    } finally {
      setActionLoading("");
    }
  };

  const handlePublishStatus = async (campaign, status) => {
    if (!campaign?.id) return;

    try {
      setActionLoading(`publish-${campaign.id}`);
      setError("");
      setSuccess("");

      const response = await api.put(
        `/campaigns/${campaign.id}/publish-status`,
        { status }
      );

      if (!response?.data?.success) {
        throw new Error(
          response?.data?.message ||
            "Unable to update publishing status."
        );
      }

      setSuccess("Campaign publishing status updated.");
      await fetchCampaigns();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to update publishing status."
      );
    } finally {
      setActionLoading("");
    }
  };

  const toggleSelectAll = () => {
    if (selectedCampaigns.length === campaigns.length) {
      setSelectedCampaigns([]);
      return;
    }

    setSelectedCampaigns(
      campaigns
        .map((campaign) => campaign.id)
        .filter(Boolean)
    );
  };

  const toggleSelectCampaign = (id) => {
    setSelectedCampaigns((previous) =>
      previous.includes(id)
        ? previous.filter((campaignId) => campaignId !== id)
        : [...previous, id]
    );
  };

  const clearFilters = () => {
    setSearch("");
    setPlatform("");
    setObjective("");
    setActiveFilter("");
    setPublishStatus("");
    setPage(1);
  };

  const hasFilters =
    search ||
    platform ||
    objective ||
    activeFilter !== "" ||
    publishStatus;

  const allSelected =
    campaigns.length > 0 &&
    selectedCampaigns.length === campaigns.length;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Campaigns"
          pageTitle="Marketing"
        />

        {error && (
          <Alert
            color="danger"
            toggle={() => setError("")}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            color="success"
            toggle={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        <Row className="mb-3">
          <Col>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div>
                <h4 className="mb-1">Campaigns</h4>
                <p className="text-muted mb-0">
                  Manage education marketing campaigns and lead attribution.
                </p>
              </div>

              <div className="d-flex gap-2">
                <Button
                  color="light"
                  onClick={fetchCampaigns}
                  disabled={loading}
                >
                  <i className="ri-refresh-line me-1" />
                  Refresh
                </Button>

                <Button
                  color="light"
                  onClick={() => navigate("/marketing/assets")}
                >
                  <i className="ri-image-line me-1" />
                  Marketing Assets
                </Button>

                <Button
                  color="primary"
                  onClick={() => navigate("/campaigns/create")}
                >
                  <i className="ri-add-line me-1" />
                  Create Campaign
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          <Col xl={3} md={6}>
            <Card className="card-animate">
              <CardBody>
                <p className="text-muted mb-1">Total Campaigns</p>
                <h4 className="mb-0">
                  {pagination.total ?? stats.total}
                </h4>
              </CardBody>
            </Card>
          </Col>

          <Col xl={3} md={6}>
            <Card className="card-animate">
              <CardBody>
                <p className="text-muted mb-1">Active</p>
                <h4 className="mb-0">{stats.active}</h4>
              </CardBody>
            </Card>
          </Col>

          <Col xl={3} md={6}>
            <Card className="card-animate">
              <CardBody>
                <p className="text-muted mb-1">Leads</p>
                <h4 className="mb-0">{stats.leads}</h4>
              </CardBody>
            </Card>
          </Col>

          <Col xl={3} md={6}>
            <Card className="card-animate">
              <CardBody>
                <p className="text-muted mb-1">Admissions</p>
                <h4 className="mb-0">{stats.admissions}</h4>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Card>
          <CardBody>
            <Row className="g-3">
              <Col lg={3} md={6}>
                <Label>Search</Label>
                <Input
                  value={search}
                  placeholder="Search campaigns..."
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                />
              </Col>

              <Col lg={2} md={6}>
                <Label>Platform</Label>
                <Input
                  type="select"
                  value={platform}
                  onChange={(event) => {
                    setPlatform(event.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All Platforms</option>
                  {PLATFORM_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col lg={2} md={6}>
                <Label>Objective</Label>
                <Input
                  type="select"
                  value={objective}
                  onChange={(event) => {
                    setObjective(event.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All Objectives</option>
                  {OBJECTIVE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col lg={2} md={6}>
                <Label>Status</Label>
                <Input
                  type="select"
                  value={activeFilter}
                  onChange={(event) => {
                    setActiveFilter(event.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Input>
              </Col>

              <Col lg={2} md={6}>
                <Label>Publishing</Label>
                <Input
                  type="select"
                  value={publishStatus}
                  onChange={(event) => {
                    setPublishStatus(event.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All</option>
                  {PUBLISH_STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col lg={1} className="d-flex align-items-end">
                {hasFilters && (
                  <Button
                    color="light"
                    className="w-100"
                    onClick={clearFilters}
                  >
                    Clear
                  </Button>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="card-title mb-1">
                  Campaign List
                </h5>
                <p className="text-muted mb-0">
                  {selectedCampaigns.length > 0
                    ? `${selectedCampaigns.length} selected`
                    : `${campaigns.length} campaigns shown`}
                </p>
              </div>
            </div>

            <div className="table-responsive">
              <Table
                hover
                className="align-middle table-nowrap mb-0"
              >
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 40 }}>
                      <Input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th>Campaign</th>
                    <th>Platform</th>
                    <th>Dates</th>
                    <th>Budget</th>
                    <th>Leads</th>
                    <th>Applications</th>
                    <th>Admissions</th>
                    <th>Publishing</th>
                    <th>Status</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="11" className="text-center py-5">
                        <Spinner size="sm" className="me-2" />
                        Loading campaigns...
                      </td>
                    </tr>
                  ) : campaigns.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="text-center py-5">
                        <h5>No campaigns found</h5>
                        <p className="text-muted mb-3">
                          Create your first campaign to start tracking marketing
                          performance.
                        </p>
                        <Button
                          color="primary"
                          onClick={() => navigate("/campaigns/create")}
                        >
                          <i className="ri-add-line me-1" />
                          Create Campaign
                        </Button>
                      </td>
                    </tr>
                  ) : (
                    campaigns.map((campaign) => (
                      <tr key={campaign.id}>
                        <td>
                          <Input
                            type="checkbox"
                            checked={selectedCampaigns.includes(
                              campaign.id
                            )}
                            onChange={() =>
                              toggleSelectCampaign(campaign.id)
                            }
                          />
                        </td>

                        <td>
                          <Link
                            to={`/campaigns/${campaign.id}`}
                            className="fw-semibold text-primary"
                          >
                            {campaign.name}
                          </Link>

                          {campaign.code && (
                            <div className="text-muted fs-12 mt-1">
                              {campaign.code}
                            </div>
                          )}

                          {campaign.offer && (
                            <div
                              className="text-muted fs-12 mt-1 text-truncate"
                              style={{ maxWidth: 220 }}
                            >
                              {campaign.offer}
                            </div>
                          )}
                        </td>

                        <td>
                          <Badge color="light">
                            {campaign.platform || "-"}
                          </Badge>
                        </td>

                        <td>
                          <div>{formatDate(campaign.startDate)}</div>
                          {campaign.endDate && (
                            <div className="text-muted fs-12">
                              to {formatDate(campaign.endDate)}
                            </div>
                          )}
                        </td>

                        <td>
                          {formatCurrency(campaign.budget)}

                          {Number(campaign.spend || 0) > 0 && (
                            <div className="text-muted fs-12">
                              Spent {formatCurrency(campaign.spend)}
                            </div>
                          )}
                        </td>

                        <td className="fw-semibold">
                          {campaign.leadCount || 0}
                        </td>

                        <td>
                          {campaign.applicationCount || 0}
                        </td>

                        <td>
                          {campaign.admissionCount || 0}
                        </td>

                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {getPublishBadge(
                              campaign.metaPublishStatus
                            )}

                            <span className="text-muted fs-12 d-none d-xl-inline">
                              {getPublishingLabel(
                                campaign.platform
                              )}
                            </span>
                          </div>
                        </td>

                        <td>
                          {getActiveBadge(campaign.isActive)}
                        </td>

                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-1">
                            <Button
                              color="light"
                              size="sm"
                              title="View"
                              onClick={() =>
                                navigate(
                                  `/campaigns/${campaign.id}`
                                )
                              }
                            >
                              <i className="ri-eye-line" />
                            </Button>

                            <Button
                              color="light"
                              size="sm"
                              title="Edit"
                              onClick={() =>
                                navigate(
                                  `/campaigns/${campaign.id}/edit`
                                )
                              }
                            >
                              <i className="ri-pencil-line" />
                            </Button>

                            {campaign.isActive ? (
                              <Button
                                color="light"
                                size="sm"
                                title="Deactivate"
                                disabled={
                                  actionLoading ===
                                  `deactivate-${campaign.id}`
                                }
                                onClick={() =>
                                  performCampaignAction(
                                    campaign,
                                    "deactivate"
                                  )
                                }
                              >
                                <i className="ri-pause-line" />
                              </Button>
                            ) : (
                              <Button
                                color="light"
                                size="sm"
                                title="Activate"
                                disabled={
                                  actionLoading ===
                                  `activate-${campaign.id}`
                                }
                                onClick={() =>
                                  performCampaignAction(
                                    campaign,
                                    "activate"
                                  )
                                }
                              >
                                <i className="ri-play-line" />
                              </Button>
                            )}

                            <Button
                              color="light"
                              size="sm"
                              title="Delete"
                              disabled={
                                actionLoading ===
                                `delete-${campaign.id}`
                              }
                              onClick={() =>
                                performCampaignAction(
                                  campaign,
                                  "delete"
                                )
                              }
                            >
                              <i className="ri-delete-bin-line text-danger" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            {!loading &&
              campaigns.length > 0 &&
              pagination.pages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted">
                    Page {pagination.page || page} of {pagination.pages}
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      color="light"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() =>
                        setPage((previous) =>
                          Math.max(1, previous - 1)
                        )
                      }
                    >
                      Previous
                    </Button>

                    <Button
                      color="light"
                      size="sm"
                      disabled={page >= pagination.pages}
                      onClick={() =>
                        setPage((previous) =>
                          Math.min(
                            pagination.pages,
                            previous + 1
                          )
                        )
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default Campaigns;
